if (!customElements.get('cropcrates-cart-upsell')) {
  class CropcratesCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.loadRecommendations();

      this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
    }

    handleClick = async (event) => {
      const button = event.target.closest('[data-upsell-add]');
      if (!button) return;

      event.preventDefault();

      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;

      const originalText = button.textContent;

      button.disabled = true;
      button.textContent = '…';

      try {
        if (!window.Shopify?.actions?.updateCart) {
          throw new Error('Native Shopify cart action unavailable.');
        }

        const result = await window.Shopify.actions.updateCart(
          {
            lines: [
              {
                merchandiseId: String(variantId),
                quantity: 1,
              },
            ],
          },
          {
            event: {
              context: 'standard-action',
              detail: {
                source: 'cropcrates-cart-upsell',
                sourceId: 'cart-drawer',
              },
            },
          }
        );

        if (result?.userErrors?.length) {
          throw new Error(
            result.userErrors.map((error) => error.message).join(', ')
          );
        }

        button.textContent = '✓';
      } catch (error) {
        console.error('[Cropcrates cart upsell]', error);

        button.textContent = '×';

        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1500);
      }
    };

    async loadRecommendations() {
      const productId = this.dataset.productId;
      const track = this.querySelector('[data-upsell-track]');

      if (!productId || !track) {
        this.hidden = true;
        return;
      }

      try {
        const root = window.Shopify?.routes?.root || '/';

        const response = await fetch(
          `${root}recommendations/products.json?product_id=${encodeURIComponent(
            productId
          )}&limit=8&intent=related`
        );

        if (!response.ok) {
          throw new Error('Recommendations request failed.');
        }

        const data = await response.json();

        const productsInCart = new Set(
          (this.dataset.cartProductIds || '')
            .split(',')
            .filter(Boolean)
            .map(String)
        );

        const products = (data.products || [])
          .filter((product) => {
            return (
              product.available &&
              !productsInCart.has(String(product.id))
            );
          })
          .slice(0, 6);

        if (!products.length) {
          this.hidden = true;
          return;
        }

        track.innerHTML = products
          .map((product) => this.renderProduct(product))
          .join('');
          requestAnimationFrame(() => {
  this.initUpsellScrollbar();
});
      } catch (error) {
        console.error('[Cropcrates cart upsell]', error);
        this.hidden = true;
      }
    }

    initUpsellScrollbar() {
  const track = this.querySelector('[data-upsell-track]');
  const bar = this.querySelector('[data-upsell-scrollbar]');
  const thumb = this.querySelector('[data-upsell-scrollbar-thumb]');

  if (!track || !bar || !thumb) return;

  const update = () => {
    const maxScroll = track.scrollWidth - track.clientWidth;

    if (maxScroll <= 1) {
      bar.hidden = true;
      return;
    }

    bar.hidden = false;

    const visibleRatio = track.clientWidth / track.scrollWidth;
    const thumbWidth = Math.max(18, visibleRatio * 100);

    thumb.style.width = `${thumbWidth}%`;

    const availableTravel =
      bar.clientWidth - thumb.offsetWidth;

    const progress =
      maxScroll > 0 ? track.scrollLeft / maxScroll : 0;

    thumb.style.transform =
      `translateX(${availableTravel * progress}px)`;
  };

  track.addEventListener('scroll', update, {
    passive: true
  });

  /* Click somewhere on the bar to jump there */
  bar.addEventListener('click', (event) => {
    if (event.target === thumb) return;

    const rect = bar.getBoundingClientRect();

    const position =
      Math.max(
        0,
        Math.min(
          1,
          (event.clientX - rect.left) / rect.width
        )
      );

    const maxScroll =
      track.scrollWidth - track.clientWidth;

    track.scrollTo({
      left: maxScroll * position,
      behavior: 'smooth'
    });
  });

  /* Drag the black indicator */
  let dragging = false;
  let startX = 0;
  let startScroll = 0;

  thumb.addEventListener('pointerdown', (event) => {
    dragging = true;
    startX = event.clientX;
    startScroll = track.scrollLeft;

    thumb.setPointerCapture(event.pointerId);
  });

  thumb.addEventListener('pointermove', (event) => {
    if (!dragging) return;

    const maxScroll =
      track.scrollWidth - track.clientWidth;

    const maxThumbTravel =
      bar.clientWidth - thumb.offsetWidth;

    if (maxThumbTravel <= 0) return;

    const deltaX = event.clientX - startX;

    track.scrollLeft =
      startScroll +
      deltaX * (maxScroll / maxThumbTravel);
  });

  const stopDragging = () => {
    dragging = false;
  };

  thumb.addEventListener('pointerup', stopDragging);
  thumb.addEventListener('pointercancel', stopDragging);

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(update);
    observer.observe(track);
  }

  update();
}

    renderProduct(product) {
      const variant =
        product.variants?.find((variant) => variant.available) ||
        product.variants?.[0];

      if (!variant) return '';

      const image = this.getImage(product);
      const price = this.formatMoney(variant.price ?? product.price);
      const title = this.escapeHTML(product.title || '');
      const url = this.escapeHTML(product.url || '#');

      return `
        <article class="cropcrates-cart-upsell__card">

          <a
            href="${url}"
            class="cropcrates-cart-upsell__image-link"
            aria-label="${title}"
          >
            ${
              image
                ? `<img
                    src="${this.escapeHTML(image)}"
                    alt="${title}"
                    class="cropcrates-cart-upsell__image"
                    loading="lazy"
                  >`
                : ''
            }
          </a>

          <div class="cropcrates-cart-upsell__meta">
            <span class="cropcrates-cart-upsell__price">
              ${price}
            </span>

            <button
              type="button"
              class="cropcrates-cart-upsell__add"
              data-upsell-add
              data-variant-id="${variant.id}"
              aria-label="Add ${title} to bag"
            >
              +
            </button>
          </div>

        </article>
      `;
    }

    getImage(product) {
      if (typeof product.featured_image === 'string') {
        return product.featured_image;
      }

      if (product.featured_image?.url) {
        return product.featured_image.url;
      }

      if (product.featured_image?.src) {
        return product.featured_image.src;
      }

      const firstImage = product.images?.[0];

      if (typeof firstImage === 'string') {
        return firstImage;
      }

      return firstImage?.src || firstImage?.url || '';
    }

    formatMoney(value) {
      const currency =
        window.Shopify?.currency?.active || 'USD';

      const numericValue = Number(value);

      if (!Number.isFinite(numericValue)) return '';

      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-US',
        {
          style: 'currency',
          currency,
        }
      ).format(numericValue / 100);
    }

    escapeHTML(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  customElements.define(
    'cropcrates-cart-upsell',
    CropcratesCartUpsell
  );
}